import { useState } from 'react'

export default function AccountForm({ account, onSubmit, onCancel, loading }) {
  const [formData, setFormData] = useState({
    Name: account?.Name || '',
    InternalIdentifier: account?.InternalIdentifier || '',
    DefaultLength: account?.DefaultLength ?? 6,
    DefaultExpiry: account?.DefaultExpiry ?? 10,
    ResendCooldownSeconds: account?.ResendCooldownSeconds ?? 60,
    MaxRequestsPerDaySms: account?.MaxRequestsPerDaySms ?? '',
    MaxRequestsPerDayEmail: account?.MaxRequestsPerDayEmail ?? '',
    MaxRequestsPerDayWhatsApp: account?.MaxRequestsPerDayWhatsApp ?? '',
    MaxRequestsPerDayPerAUserSms: account?.MaxRequestsPerDayPerAUserSms ?? '',
    MaxRequestsPerDayPerAUserEmail: account?.MaxRequestsPerDayPerAUserEmail ?? '',
    MaxRequestsPerDayPerAUserWhatsApp: account?.MaxRequestsPerDayPerAUserWhatsApp ?? '',
    SmsApiKey: account?.SmsApiKey || '',
    EmailApiKey: account?.EmailApiKey || '',
    WhatsAppApiKey: account?.WhatsAppApiKey || '',
    SmsTemplateIdentifier: account?.SmsTemplateIdentifier || '',
    EmailTemplateIdentifier: account?.EmailTemplateIdentifier || '',
    WhatsAppTemplateIdentifier: account?.WhatsAppTemplateIdentifier || '',
    IsDeleted: false,
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    const emptyToNull = (v) => (v === '' || v === null || v === undefined ? null : v)
    const intOrNull = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10))
    onSubmit({
      ...formData,
      DefaultLength: parseInt(formData.DefaultLength, 10),
      DefaultExpiry: parseInt(formData.DefaultExpiry, 10),
      ResendCooldownSeconds: parseInt(formData.ResendCooldownSeconds, 10),
      MaxRequestsPerDaySms: intOrNull(formData.MaxRequestsPerDaySms),
      MaxRequestsPerDayEmail: intOrNull(formData.MaxRequestsPerDayEmail),
      MaxRequestsPerDayWhatsApp: intOrNull(formData.MaxRequestsPerDayWhatsApp),
      MaxRequestsPerDayPerAUserSms: intOrNull(formData.MaxRequestsPerDayPerAUserSms),
      MaxRequestsPerDayPerAUserEmail: intOrNull(formData.MaxRequestsPerDayPerAUserEmail),
      MaxRequestsPerDayPerAUserWhatsApp: intOrNull(formData.MaxRequestsPerDayPerAUserWhatsApp),
      SmsApiKey: emptyToNull(formData.SmsApiKey),
      EmailApiKey: emptyToNull(formData.EmailApiKey),
      WhatsAppApiKey: emptyToNull(formData.WhatsAppApiKey),
      SmsTemplateIdentifier: emptyToNull(formData.SmsTemplateIdentifier),
      EmailTemplateIdentifier: emptyToNull(formData.EmailTemplateIdentifier),
      WhatsAppTemplateIdentifier: emptyToNull(formData.WhatsAppTemplateIdentifier),
      InternalIdentifier: emptyToNull(formData.InternalIdentifier),
    })
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Account Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text" name="Name" value={formData.Name} onChange={handleChange} required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="e.g., OTP Production"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Internal Identifier</label>
          <input
            type="text" name="InternalIdentifier" value={formData.InternalIdentifier} onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="e.g., acme-otp"
          />
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">OTP Defaults</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Code Length</label>
            <input
              type="number" name="DefaultLength" value={formData.DefaultLength} onChange={handleChange} min={4} max={10} required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Expiry (minutes)</label>
            <input
              type="number" name="DefaultExpiry" value={formData.DefaultExpiry} onChange={handleChange} min={1} required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Resend Cooldown (s)</label>
            <input
              type="number" name="ResendCooldownSeconds" value={formData.ResendCooldownSeconds} onChange={handleChange} min={0} required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Rate Limits (per day, leave blank for unlimited)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Account-wide SMS</label>
            <input type="number" name="MaxRequestsPerDaySms" value={formData.MaxRequestsPerDaySms} onChange={handleChange} min={0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Account-wide Email</label>
            <input type="number" name="MaxRequestsPerDayEmail" value={formData.MaxRequestsPerDayEmail} onChange={handleChange} min={0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Account-wide WhatsApp</label>
            <input type="number" name="MaxRequestsPerDayWhatsApp" value={formData.MaxRequestsPerDayWhatsApp} onChange={handleChange} min={0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Per-user SMS</label>
            <input type="number" name="MaxRequestsPerDayPerAUserSms" value={formData.MaxRequestsPerDayPerAUserSms} onChange={handleChange} min={0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Per-user Email</label>
            <input type="number" name="MaxRequestsPerDayPerAUserEmail" value={formData.MaxRequestsPerDayPerAUserEmail} onChange={handleChange} min={0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Per-user WhatsApp</label>
            <input type="number" name="MaxRequestsPerDayPerAUserWhatsApp" value={formData.MaxRequestsPerDayPerAUserWhatsApp} onChange={handleChange} min={0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Downstream Channel API Keys</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          OTP delegates send to Email/SMS/WhatsApp channels — paste the relevant channel API keys here.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">SMS API Key</label>
            <input type="text" name="SmsApiKey" value={formData.SmsApiKey} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
              placeholder="SMS channel API key" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email API Key</label>
            <input type="text" name="EmailApiKey" value={formData.EmailApiKey} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
              placeholder="Email channel API key" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">WhatsApp API Key</label>
            <input type="text" name="WhatsAppApiKey" value={formData.WhatsAppApiKey} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
              placeholder="WhatsApp channel API key" />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Template Identifiers (optional)</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">SMS Template UUID</label>
            <input type="text" name="SmsTemplateIdentifier" value={formData.SmsTemplateIdentifier} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
              placeholder="UUID from SMS templates" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email Template UUID</label>
            <input type="text" name="EmailTemplateIdentifier" value={formData.EmailTemplateIdentifier} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
              placeholder="UUID from Email templates" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">WhatsApp Template UUID</label>
            <input type="text" name="WhatsAppTemplateIdentifier" value={formData.WhatsAppTemplateIdentifier} onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
              placeholder="UUID from WhatsApp templates" />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button" onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit" disabled={loading}
          className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
        >
          {loading ? 'Saving...' : account ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  )
}
